"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { toast } from "sonner";
import { useSupabase } from "@/lib/auth/context";

/**
 * Sign-up page with Supabase Auth
 * Supports email/password and Google OAuth
 * Preserves ?intent=vendor and ?redirect_url params
 */
export default function SignUpPage() {
  const _router = useRouter();
  const sp = useSearchParams();
  const supabase = useSupabase();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Preserve intent and redirect_url
  const intent = sp.get("intent") ?? (sp.get("vendor") ? "vendor" : null);
  const redirectUrl = sp.get("redirect_url");
  const safeRedirect = redirectUrl?.startsWith("/") ? redirectUrl : null;

  const qs = new URLSearchParams();
  if (intent === "vendor") qs.set("intent", "vendor");
  if (safeRedirect) qs.set("redirect_url", safeRedirect);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  // After email confirmation, go to post-auth which routes based on role
  // For regular users without a redirect_url, they'll end up at /dashboard
  // With vendor intent, they'll go to vendor onboarding
  const postAuth = `/post-auth${suffix}`;
  const signInUrl = `/sign-in${suffix}`;

  // For immediate redirect after signup (if email confirmation disabled)
  // Go to homepage for regular users, post-auth for vendors
  const immediateRedirect = intent === "vendor" ? postAuth : safeRedirect || "/";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    // After email confirmation, redirect to homepage (or post-auth for vendors)
    const emailRedirectTarget = intent === "vendor" ? postAuth : safeRedirect || "/";

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          role: intent === "vendor" ? "vendor" : "user",
        },
        emailRedirectTo: `${window.location.origin}/auth/callback?redirect_url=${encodeURIComponent(emailRedirectTarget)}`,
      },
    });

    if (error) {
      console.error("[SignUp] Error:", error.message);
      toast.error(error.message);
      setLoading(false);
      return;
    }

    console.log("[SignUp] Success:", { hasSession: !!data.session, user: data.user?.email });

    // If session exists, user is immediately signed in (email confirmation disabled)
    // Use full page redirect to ensure server sees the new session cookies
    if (data.session) {
      console.log("[SignUp] Has session, redirecting to:", immediateRedirect);
      window.location.href = immediateRedirect;
      return;
    }

    // Email confirmation is required - redirect to sign-in page with message
    console.log("[SignUp] No session, redirecting to sign-in:", signInUrl);
    toast.success("Account created! Check your email for the confirmation link.");
    window.location.href = signInUrl;
  };

  const handleGoogleSignUp = async () => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback?redirect_url=${encodeURIComponent(postAuth)}`,
      },
    });

    if (error) {
      toast.error(error.message);
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-[70vh] place-items-center p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-semibold">Create your account</h1>
          <p className="text-muted-foreground mt-2">
            {intent === "vendor"
              ? "Join as a vendor to offer your services."
              : "Get started with Starlit Passage today."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium mb-1">
              Full Name
            </label>
            <input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              autoComplete="name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent text-black"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent text-black"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent text-black"
              placeholder="At least 6 characters"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gray-900 text-white py-2.5 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loading ? "Creating account..." : "Create account"}
          </button>
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
          </div>
        </div>

        <button
          type="button"
          onClick={handleGoogleSignUp}
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 border border-gray-300 py-2.5 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Google
        </button>

        <p className="text-center text-sm">
          Already have an account?{" "}
          <Link href={signInUrl} className="font-medium underline underline-offset-4">
            Sign in
          </Link>
        </p>

        <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
          By creating an account, you agree to the{" "}
          <Link href="/terms" className="underline underline-offset-4">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link href="/privacy" className="underline underline-offset-4">
            Privacy Policy
          </Link>
          , and acknowledge our{" "}
          <Link href="/cookies" className="underline underline-offset-4">
            Cookie Policy
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
